import React from "react";
import {
  InternalLinkConfig,
  allInternalLinks,
  getContextualLinks,
} from "@/utils/internal-links-config";

interface InternalLinkProps {
  linkKey?: string; // Key from internal-links-config
  href?: string; // Custom URL if not using linkKey
  anchorText?: string; // Custom anchor text if not using linkKey
  className?: string;
  children?: React.ReactNode;
  title?: string;
  onClick?: () => void;
  openInNewTab?: boolean;
}

export function InternalLink({
  linkKey,
  href,
  anchorText,
  className = "",
  children,
  title,
  onClick,
  openInNewTab = false,
}: InternalLinkProps) {
  // Get link configuration if linkKey is provided
  const linkConfig = linkKey ? allInternalLinks[linkKey] : null;

  // Determine final URL and anchor text
  const finalUrl = linkConfig?.url || href || "/";
  const finalAnchorText =
    children || linkConfig?.anchorText || anchorText || "Learn more";
  const baseTitle = title ?? linkConfig?.description;
  const finalTitle =
    typeof baseTitle === "string"
      ? baseTitle
      : typeof finalAnchorText === "string"
        ? finalAnchorText
        : undefined;

  // Base classes for SEO-friendly internal links
  const baseClasses =
    "text-blue-600 hover:text-blue-800 transition-colors underline decoration-blue-300 hover:decoration-blue-500";
  const finalClasses = `${baseClasses} ${className}`.trim();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      onClick();
    }

    // Track internal link clicks for analytics if available
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "click", {
        event_category: "Internal Link",
        event_label: finalUrl,
        link_text: finalAnchorText,
      });
    }
  };

  return (
    <a
      href={finalUrl}
      className={finalClasses}
      title={finalTitle}
      onClick={handleClick}
      target={openInNewTab ? "_blank" : undefined}
      rel={openInNewTab ? "noopener noreferrer" : undefined}
    >
      {finalAnchorText}
    </a>
  );
}

// Specialized components for specific contexts

interface LocationLinkProps {
  city: string;
  variant?: "taxi" | "taxis" | "service" | "city";
  className?: string;
}

export function LocationLink({
  city,
  variant = "taxi",
  className,
}: LocationLinkProps) {
  const cityLower = city.toLowerCase();
  const linkKey = `${cityLower.replace(/\s+/g, "_")}_${variant === "city" ? "taxi" : variant}`;
  const slug = cityLower.replace(/\s+/g, "-");

  // Check if this location has a config entry
  const linkConfig = allInternalLinks[linkKey];

  // If we have a config entry, use that. Otherwise, generate a default URL.
  // All locations use canonical /taxi/ path, regardless of whether they're cities or towns
  let href: string;

  if (linkConfig?.url) {
    href = linkConfig.url;
  } else {
    // For unconfigured locations, use canonical /taxi/ path
    href = `/taxi/${slug}`;
  }

  const anchorText = variant === "city" ? city : `${city} ${variant}`;

  return (
    <InternalLink
      linkKey={variant === "city" ? undefined : linkKey}
      href={href}
      anchorText={anchorText}
      className={className}
    />
  );
}

interface AirportLinkProps {
  airportCode: string;
  airportName?: string;
  variant?: "transfer" | "taxi" | "transport";
  className?: string;
  children?: React.ReactNode;
}

export function AirportLink({
  airportCode,
  airportName,
  variant = "transfer",
  className,
  children,
}: AirportLinkProps) {
  const codeLower = airportCode.toLowerCase();
  const linkKey = `${codeLower.replace(/[^a-z]/g, "")}_${variant}`;
  const displayName = airportName || airportCode;

  return (
    <InternalLink
      linkKey={linkKey}
      href={`/airports/${codeLower}`}
      anchorText={children ? undefined : `${displayName} airport ${variant}`}
      className={className}
    >
      {children}
    </InternalLink>
  );
}

interface RouteLinkProps {
  fromLocation: string;
  toLocation: string;
  fromType?: "city" | "airport";
  toType?: "city" | "airport";
  className?: string;
  children?: React.ReactNode;
}

export function RouteLink({
  fromLocation,
  toLocation,
  fromType = "city",
  toType = "airport",
  className,
  children,
}: RouteLinkProps) {
  const fromLower = fromLocation.toLowerCase().replace(/[^a-z]/g, "");
  const toLower = toLocation.toLowerCase().replace(/[^a-z]/g, "");

  // Try to find exact route link first
  let linkKey = `${fromLower}_to_${toLower}`;
  let linkConfig = allInternalLinks[linkKey];

  // If not found, try reverse route
  if (!linkConfig) {
    linkKey = `${toLower}_to_${fromLower}`;
    linkConfig = allInternalLinks[linkKey];
  }

  // Determine target URL based on destination type
  const targetUrl =
    toType === "airport" ? `/airports/${toLower}` : `/taxi/${toLower}`;

  const anchorText = `${fromLocation} to ${toLocation} taxi`;

  return (
    <InternalLink
      linkKey={linkConfig ? linkKey : undefined}
      href={linkConfig ? undefined : targetUrl}
      anchorText={linkConfig ? undefined : children ? undefined : anchorText}
      className={className}
    >
      {children}
    </InternalLink>
  );
}

// Service link component
interface ServiceLinkProps {
  service:
    | "taxi"
    | "taxis"
    | "airport_transfers"
    | "business_transport"
    | "coverage_areas";
  className?: string;
  children?: React.ReactNode;
}

export function ServiceLink({
  service,
  className,
  children,
}: ServiceLinkProps) {
  return (
    <InternalLink linkKey={service} className={className}>
      {children}
    </InternalLink>
  );
}

// Near me link component
interface NearMeLinkProps {
  type?: "general" | "airport";
  className?: string;
}

export function NearMeLink({ type = "general", className }: NearMeLinkProps) {
  const linkKey = type === "airport" ? "airport_taxi_near_me" : "taxi_near_me";

  return <InternalLink linkKey={linkKey} className={className} />;
}

// Contextual links section component
interface ContextualLinksProps {
  pageType: "homepage" | "city" | "airport" | "service";
  location?: string;
  airportCode?: string;
  className?: string;
  showTitle?: boolean;
  maxLinks?: number;
}

export function ContextualLinks({
  pageType,
  location,
  airportCode,
  className = "",
  showTitle = true,
  maxLinks = 6,
}: ContextualLinksProps) {
  const links = getContextualLinks(pageType, location, airportCode).slice(
    0,
    maxLinks,
  );

  if (links.length === 0) return null;

  return (
    <div className={`space-y-3 ${className}`}>
      {showTitle && (
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Related Services
        </h3>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {links.map((link, index) => (
          <InternalLink
            key={index}
            href={link.url}
            anchorText={link.anchorText}
            title={link.description}
            className="block text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-2 rounded transition-colors"
          />
        ))}
      </div>
    </div>
  );
}

// Bulk link insertion component for content sections
interface LinkifyContentProps {
  children: React.ReactNode;
  maxLinks?: number;
  context?: "city" | "airport" | "service";
  location?: string;
}

export function LinkifyContent({
  children,
  maxLinks = 3,
  context,
  location,
}: LinkifyContentProps) {
  // This component can be enhanced to automatically detect and linkify keywords in content
  // For now, it's a wrapper that renders children with contextual links below
  const links =
    context && location
      ? getContextualLinks(context, location).slice(0, maxLinks)
      : [];

  return (
    <div>
      {children}
      {links.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap gap-3">
            {links.map((link, index) => (
              <InternalLink
                key={index}
                href={link.url}
                anchorText={link.anchorText}
                className="text-sm bg-blue-50 text-blue-700 px-3 py-1 rounded-full hover:bg-blue-100"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default InternalLink;
